import { FunctionComponent, useCallback, useContext, useMemo, useState } from "react";
import styles from "./MarkAsSold.module.css";
import {
  CustomTextField,
  InputPropsStyleForInvoice,
  checking,
  formatNumberWithCommas,
  listTicketsRowType,
  roundToFixed,
  splitLocation,
} from "../../../helper";
import {
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { MARK_AS_SOLD } from "./api/MarkAsSoldQuery";
import ToasterWithMessage from "../toaster";
import { globalContext } from "../../../Provider/AppProvider";
import images from '../../../assets/images/index'
import {
  getMarketplaceFeePercent,
  MARKETPLACE_OPTIONS,
  BASELINE_MARKETPLACE_PERCENT,
} from "../../../constants/marketplace-fees";

interface MarkAsSoldProps {
  row: listTicketsRowType;
  handleCloseDialog: () => void;
  setColumnTypes: (value: string) => void;
  setSortType: (value: string) => void;
  /** Super users: total client fee % before marketplace; default sellers: null */
  superClientPercent: number | null;
  /** `super`: QuickAsyst % = max(0, superClientPercent − marketplace%). `default`: QuickAsyst % = quick_cut config */
  feeMode: "super" | "default";
  /** Earning config quick_cut % (used when feeMode is default) */
  defaultQuickCutPercent: number;
}

interface submitData {
  amount: string,
  cutamount: number,
  finalAmount: number
}
const AmountField = CustomTextField();

function effectiveQuickCutPercent(
  marketplaceKey: string,
  feeMode: "super" | "default",
  superClientPercent: number | null,
  defaultQuickCutPercent: number,
): number {
  const mp = marketplaceKey ? getMarketplaceFeePercent(marketplaceKey) : null;
  if (mp === null) return 0;
  if (feeMode === "super" && superClientPercent != null) {
    return Math.max(0, superClientPercent - mp);
  }
  return Math.max(0, defaultQuickCutPercent + (BASELINE_MARKETPLACE_PERCENT - mp));
}

const MarkAsSold: FunctionComponent<MarkAsSoldProps> = ({
  row,
  handleCloseDialog,
  setColumnTypes,
  setSortType,
  superClientPercent,
  feeMode,
  defaultQuickCutPercent,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      amount: "",
      cutamount: 0.0,
      finalAmount: 0.0,
    },
    mode: "onTouched",
  });
  const [showIcon, setShowIcon] = useState(false);
  const { error ,success} = ToasterWithMessage();
  const {globalState,handleGlobalState} = useContext<any>(globalContext);
  const [errorAmount, setErroramount] = useState<string>("")
  const [errorAmountForlist, setErroramountList] = useState<string>("")
  const [errorAmountForfinal, setErroramountfinal] = useState<string>("")
  const [errorMarketplace, setErrorMarketplace] = useState<string>("")
  const [amount,setAmount] = useState<string>("")
  const [marketplace, setMarketplace] = useState<string>("");
  const {CloseIcon} = images;

  const expectedReceivedAmount = useMemo((): number | null => {
    const mp = marketplace ? getMarketplaceFeePercent(marketplace) : null;
    if (mp === null || !row?.tp_list_price) return null;
    return Math.round(row.tp_list_price * (100 - mp)) / 100;
  }, [marketplace, row?.tp_list_price]);

  const recalcFromAmount = useCallback(
    (rawAmount: string, marketplaceKeyOverride?: string) => {
      const mpKey =
        marketplaceKeyOverride !== undefined ? marketplaceKeyOverride : marketplace;
      const pct = effectiveQuickCutPercent(
        mpKey,
        feeMode,
        superClientPercent,
        defaultQuickCutPercent,
      );
      const parsedValue = parseFloat(rawAmount) || 0;
      const cutAmount = row?.tp_list_price * (pct / 100);
      setValue("cutamount", cutAmount);
      setValue("finalAmount", parsedValue - cutAmount);
    },
    [marketplace, feeMode, superClientPercent, defaultQuickCutPercent, row?.tp_list_price, setValue],
  );

  /** String for the amount field; matches validation `Number(roundToFixed(expected,2))` */
  const formatExpectedReceivedForInput = (listPrice: number, mp: number) => {
    const expected = Math.round(listPrice * (100 - mp)) / 100;
    return String(Number(roundToFixed(expected, 2)));
  };

  const handleFocus = () => {
    setShowIcon(true);
  };

  const handleSubmitData = async (data: submitData) => {
    if (!marketplace.trim()) {
      setErrorMarketplace("Select a marketplace");
      return;
    }
    const mp = getMarketplaceFeePercent(marketplace);
    if (mp === null) {
      setErrorMarketplace("Invalid marketplace");
      return;
    }
    const expected = row?.tp_list_price * (100 - mp) / 100;
    const price = roundToFixed(expected, 2);
    if(parseFloat(data?.amount) <= 0 ){
       setErroramount("Amount must be greater than zero")
    }else if(parseFloat(data?.amount)  > Number(row?.tp_list_price)){
      setErroramountList("Amount must be less than list price")
    }else if(parseFloat(data?.amount)!==Number(price)){
      setErroramountList("Enter received amount from sold tickets mail")
    }else if(data?.finalAmount <= 0){
      setErroramountfinal("Settle amount should be greater than zero")
    }
    else{
    const dataModify = {
      ...data,
      amount: parseFloat(data.amount),
    };
    setShowIcon(false);
    const tempState = {...globalState};
    tempState.loadingFormarkassold.soldloader = true;
    handleGlobalState(tempState)
    markAsSold({
      variables: {
        logitixAmount: dataModify.amount,
        soldAmount: dataModify.finalAmount.toFixed(2),
        ticketPlacementId: row.tp_id,
        marketplaceName: marketplace,
      },
    });
    reset({ amount: "" });
  }
  };

  const [markAsSold] = useMutation(MARK_AS_SOLD, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      success("Sold successfully");
      setColumnTypes("tp_updated_at");
      setSortType("desc");
      const tempState = { ...globalState };
      tempState.loadingFormarkassold.soldloader = false;
      handleGlobalState(tempState);
      if(data?.markAsSold?.message === "Mark as sold successful"){
        const tempState = {...globalState};
        tempState.markAsSoldTicket.markAsSold = true;
        handleGlobalState(tempState)
      }
      handleCloseDialog()
    },
    onError: (err) => {
      if(err.message === 'Ticket not listed or Invalid.'){
        error(err.message)
      }else{
      error("Something went wrong!");
      }
      handleCloseDialog()
      const tempState = { ...globalState };
      tempState.loadingFormarkassold.soldloader = false;
      handleGlobalState(tempState);
    },
  });

  const handlechangeAmount = (e : React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setErroramount("")
    setErroramountList("")
    setErroramountfinal("")
    const regexPattern = /^(0|[1-9]\d*)(\.\d{0,2})?$/;
    if (regexPattern.test(inputValue)) {
      if (parseFloat(inputValue) <= 100000 && parseFloat(inputValue) >= 0) {
        setValue("amount", inputValue);
        setAmount(inputValue);
        trigger("amount");
        recalcFromAmount(inputValue);
      }
    } else {
      setAmount(amount);
    }
  };

  const handleMarketplaceChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setMarketplace(value);
    setErrorMarketplace("");
    setErroramount("");
    setErroramountList("");
    setErroramountfinal("");
    const mp = value ? getMarketplaceFeePercent(value) : null;
    if (mp !== null && row?.tp_list_price) {
      const str = formatExpectedReceivedForInput(row.tp_list_price, mp);
      setAmount(str);
      setValue("amount", str);
      clearErrors("amount");
      trigger("amount");
      setShowIcon(true);
      recalcFromAmount(str, value);
    } else {
      setAmount("");
      setValue("amount", "");
      setValue("cutamount", 0);
      setValue("finalAmount", 0);
      clearErrors("amount");
    }
  };

  const handleKeyDown = (e :any) =>{
    if(e.key === 'Backspace'){
      if(amount.length === 1){
        setAmount("")
        setValue("cutamount", 0);
        setValue("finalAmount", 0);
      }
    }
  }

const handleResetMarkAsSold = () => {
   reset();
   setAmount("");
   setMarketplace("");
   setErrorMarketplace("");
}

  const amountPlaceholder =
    expectedReceivedAmount != null
      ? `Eg. $${formatNumberWithCommas(expectedReceivedAmount)}`
      : "Eg. $92.00";

  const quickCutPercent = effectiveQuickCutPercent(
    marketplace,
    feeMode,
    superClientPercent,
    defaultQuickCutPercent,
  );

  const listPriceNum = Number(row?.tp_list_price) || 0;
  const quickAsystFeeDollars =
    marketplace && listPriceNum
      ? Number(roundToFixed(listPriceNum * (quickCutPercent / 100), 2))
      : null;

  const enteredReceivedParsed =
    amount.trim() === "" || Number.isNaN(parseFloat(amount)) ? null : parseFloat(amount);
  const receivedForSettlement =
    enteredReceivedParsed != null ? enteredReceivedParsed : expectedReceivedAmount;
  const settlementAmount =
    receivedForSettlement != null && quickAsystFeeDollars != null
      ? Math.round((receivedForSettlement - quickAsystFeeDollars) * 100) / 100
      : null;
  const usingEnteredAmount = enteredReceivedParsed != null;

  return (
    <form onSubmit={handleSubmit(handleSubmitData)} autoComplete="off">
      <div className={styles.markAsSold}>
        <div className={styles.title}>
          <b className={styles.markAsSoldTitle}>Sold Details</b>
          <div className={styles.closeicon}>
            <img src={CloseIcon} alt="closeicon" onClick={handleCloseDialog} />
          </div>
        </div>
        <div className={styles.eventDetailsAndLogitixSold}>
          <div className={styles.eventDetails}>
            <div className={styles.eventDetails1}>Event Details</div>
            <div className={styles.eventDetails2}>
              <div className={styles.event}>
                <b className={styles.date}>Event</b>
                <b className={styles.rocketsVsMemphis}>{row?.e_name}</b>
              </div>
              <div className={styles.event}>
                <b className={styles.date}>Date</b>
                <b className={styles.rocketsVsMemphis}>
                    {checking(row?.e_date).date} /&nbsp;
                    {checking(row?.e_date).time}
                </b>
              </div>
              <div className={styles.event}>
                <b className={styles.date}>Venue</b>
                <b className={styles.rocketsVsMemphis}>
                  {splitLocation(row?.e_address).firstWord}
                  {splitLocation(row?.e_address).remainingWords}
                </b>
              </div>
              <div className={styles.event}>
                <b className={styles.date}>Ticket Placement</b>
                <b className={styles.secA}>
                  Sec : {row?.tp_section} / Row : {row?.tp_row} / Seat : {row?.tp_seat_no}
                </b>
              </div>
              <div className={styles.event}>
                <b className={styles.date}>List Price</b>
                <b className={styles.secA}>
                ${row?.tp_list_price ? formatNumberWithCommas(row?.tp_list_price) : '-'}
                </b>
              </div>
            </div>
          </div>
          <div className={styles.logitixSoldPrice}>
            <div className={styles.eventDetails1}>Ticket Sold Price</div>
            <div className={styles.bg}>
              <div className={styles.eventDetails}>
                <div className={styles.marketplaceRow}>
                  <FormControl fullWidth size="small" className={styles.marketplaceField} variant="outlined">
                    <InputLabel id="mark-as-sold-marketplace-label" shrink>
                      Marketplace
                    </InputLabel>
                    <Select<string>
                      labelId="mark-as-sold-marketplace-label"
                      label="Marketplace"
                      notched
                      value={marketplace}
                      onChange={handleMarketplaceChange}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span className={styles.marketplaceSelectPlaceholder}>
                              Select marketplace
                            </span>
                          );
                        }
                        const pct = getMarketplaceFeePercent(selected);
                        return pct != null ? `${selected} (${pct}%)` : selected;
                      }}
                      inputProps={{
                        "aria-label": "Marketplace",
                        "data-testid": "mark-as-sold-marketplace",
                      }}
                    >
                      <MenuItem value="" sx={{ display: "none" }} aria-hidden tabIndex={-1}>
                        —
                      </MenuItem>
                      {MARKETPLACE_OPTIONS.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name} ({getMarketplaceFeePercent(name)}%)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {errorMarketplace ? (
                    <span className={styles.errorMsgStyle}>{errorMarketplace}</span>
                  ) : null}
                </div>
                <div className={styles.soldPrice}>
                <div className={styles.transactionId}>
                  <b className={styles.date}>Amount received from Logitix</b>
                  <span className={styles.errorMsgStyle}>
                        { errorAmount ||  errorAmountForlist ||errors?.amount?.message }
                  </span>
                </div>
                  <div className={styles.textField}>
                    <div className={styles.container}>
                      <AmountField
                        sx={{
                          "& input": {
                            height: "0.0375em",
                          },
                        }}
                        classes={{
                          root: styles.amountdiv,
                        }}
                        {...register("amount", {
                          required: "Amount is required!"
                        })}
                        value={amount}
                        InputProps={{
                          style: InputPropsStyleForInvoice,
                          startAdornment: (
                            <InputAdornment position="start">
                              {showIcon && `$`}
                            </InputAdornment>
                          ),
                        }}
                        placeholder={watch("amount") ? "" : amountPlaceholder}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlechangeAmount(e)}
                        error={Boolean(errors?.amount?.message)}
                        onFocus={handleFocus}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.soldPrice}>
                  <b className={styles.date}>
                    QuickAsyst fee{marketplace ? ` (${quickCutPercent}% of list)` : ""}
                  </b>
                  <span className={marketplace ? styles.cutAmount : ""}>
                    {quickAsystFeeDollars != null
                      ? `-$${formatNumberWithCommas(quickAsystFeeDollars)}`
                      : "—"}
                  </span>
                </div>
                <div className={styles.line}></div>
                <div className={styles.settlementAmount}>
                  <div className={styles.settlementAmountdiv}>
                    <b className={styles.date}>Amount settled to user</b>
                    <span className={styles.errorMsgStyle}>
                      {errorAmountForfinal}
                    </span>
                  </div>
                  <div className={styles.settlementRight}>
                    <b
                      className={
                        settlementAmount != null ? styles.finalAmount : styles.b
                      }
                    >
                      {settlementAmount != null
                        ? `$${formatNumberWithCommas(settlementAmount)}`
                        : "—"}
                    </b>
                    {marketplace &&
                      receivedForSettlement != null &&
                      quickAsystFeeDollars != null && (
                        <div className={styles.settlementBreakdown}>
                          ${formatNumberWithCommas(receivedForSettlement)}{" "}
                          {usingEnteredAmount ? "received" : "expected"} − $
                          {formatNumberWithCommas(quickAsystFeeDollars)} QuickAsyst
                          fee
                          {usingEnteredAmount ? "" : " (preview)"}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.cta}>
          <div className={styles.buttons} onClick={handleResetMarkAsSold}>
            <Button
              classes={{ root: styles.separateStyleforcancel }}
              fullWidth
              disableRipple
            >
              Reset
            </Button>
          </div>
          <div className={styles.buttons1}>
            <Button
              classes={{ root: styles.separateStyle }}
              disableRipple
              fullWidth
              type="submit"
            >
              Confirm
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default MarkAsSold;
